import { HttpClient } from '@euler/lib/request';
import { SSEConnection } from '@euler/lib/request/SSEClient';
import { createAuthorizedSSEConnection } from '@euler/lib/request/authorizedSSE';
import { TokenService } from '@euler/lib/services';
import {
  ApdmCampaignVariantProposal,
  ApdmImportView,
  ApdmImportViewList,
  ApdmReadingAnswers,
  ApdmWriteCampaignVariantsRequest,
} from '@euler/model/apdm';
import {
  ApdmCampaignSummaryResult,
  ApdmReportRequest,
  ApdmSummaryStreamEvent,
} from '@euler/model/apdm/reporting';
import { TokenRefresher } from './ai-assistant.service';

export interface ApdmSummaryCallbacks {
  /** A step of the work, as it starts. */
  onNarration: (text: string) => void;
  /** Summary text as it is written. */
  onToken: (text: string) => void;
  onDone: (summary: ApdmCampaignSummaryResult) => void;
  onError: (message: string) => void;
}

export class ApdmService {
  constructor(
    private readonly api: HttpClient,
    private readonly aiApi: HttpClient,
    private readonly streams?: {
      endpoint: string;
      tokenService: TokenService;
      refreshToken?: TokenRefresher;
    },
  ) {}

  /**
   * The campaign's AI summary, streamed: the steps of the work, then the
   * text as it is written, then the saved summary. One written for the
   * same figures comes back at once as `done`.
   */
  streamCampaignSummary(
    input: ApdmReportRequest,
    callbacks: ApdmSummaryCallbacks,
  ): SSEConnection {
    if (!this.streams) throw new Error('ApdmService has no stream endpoint');
    return createAuthorizedSSEConnection({
      ...this.streams,
      path: `/api/ai/apdm/campaigns/${encodeURIComponent(
        input.campaignId,
      )}/summaries`,
      onEvent: ({ event, data }) => {
        const message = {
          event,
          data: JSON.parse(data),
        } as ApdmSummaryStreamEvent;
        if (message.event === 'narration')
          callbacks.onNarration(message.data.text);
        else if (message.event === 'token')
          callbacks.onToken(message.data.text);
        else if (message.event === 'done') callbacks.onDone(message.data);
        else if (message.event === 'error')
          callbacks.onError(message.data.message);
      },
      onError: callbacks.onError,
    });
  }
  async listImports(input: {
    afterImportId: string | null;
    limit: number;
  }): Promise<ApdmImportViewList> {
    return await this.api
      .get()
      .url('/apdm/imports', {
        afterImportId: input.afterImportId ?? undefined,
        limit: input.limit,
      })
      .future();
  }
  async uploadImport(attachmentId: string): Promise<ApdmImportView> {
    return await this.api
      .post()
      .url('/apdm/imports')
      .data({ attachmentId })
      .future();
  }
  async getImport(importId: string): Promise<ApdmImportView> {
    return await this.api
      .get()
      .url('/apdm/imports/:importId', { importId })
      .future();
  }
  async answerImport(
    importId: string,
    answers: ApdmReadingAnswers,
  ): Promise<ApdmImportView> {
    return await this.api
      .post()
      .url('/apdm/imports/:importId/answers', { importId })
      .data({ answers })
      .future();
  }
  async startImport(
    importId: string,
    requestKey: string,
  ): Promise<ApdmImportView> {
    return await this.api
      .post()
      .url('/apdm/imports/:importId/start', { importId })
      .data({ requestKey })
      .future();
  }
  async deleteImport(importId: string): Promise<ApdmImportView> {
    return await this.api
      .delete()
      .url('/apdm/imports/:importId', { importId })
      .future();
  }
  async writeVariants(
    input: ApdmWriteCampaignVariantsRequest,
  ): Promise<ApdmCampaignVariantProposal> {
    return await this.aiApi
      .post()
      .url('/apdm/campaigns/:campaignId/variant-proposals', {
        campaignId: input.campaignId,
      })
      .data({
        expectedVersion: input.expectedVersion,
        instructions: input.instructions,
      })
      .future();
  }
}
